import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Chip, useMediaQuery, useTheme, FormControl, Select, MenuItem } from '@mui/material';
import { TrendingUp, TrendingDown, People, CheckCircle, HourglassEmpty, Delete, Star } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

function FbDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('today');
  const [chartTimeRange, setChartTimeRange] = useState('today');

  const todayWeekday = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const getDateRange = () => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    switch (period) {
      case 'today': return { start: formatDate(today), end: formatDate(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: formatDate(yesterday), end: formatDate(yesterday) };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: formatDate(weekAgo), end: formatDate(today) };
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { start: formatDate(monthAgo), end: formatDate(today) };
      default: return { start: formatDate(today), end: formatDate(today) };
    }
  };

  const fetchData = async () => {
    try {
      const { start, end } = getDateRange();
      const response = await axios.get(`/admin/facebook/dashboard/stats?start_date=${start}&end_date=${end}`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!stats) return [];

    if (chartTimeRange === 'today' || chartTimeRange === 'lastWeekday') {
      // Hourly data
      const hours = Array.from({ length: 24 }, (_, i) => ({
        label: `${i.toString().padStart(2, '0')}:00`,
        leads: 0,
        leadsPrev: 0
      }));

      if (stats.hourly) {
        stats.hourly.forEach(h => {
          const hourIndex = parseInt(h.hour);
          if (hours[hourIndex]) {
            hours[hourIndex].leads = parseInt(h.leads) || 0;
          }
        });
      }

      const prevData = chartTimeRange === 'today' ? stats.hourlyYesterday : stats.hourlyLastWeekday;
      if (prevData) {
        prevData.forEach(h => {
          const hourIndex = parseInt(h.hour);
          if (hours[hourIndex]) {
            hours[hourIndex].leadsPrev = parseInt(h.leads) || 0;
          }
        });
      }

      return hours;
     } else if (chartTimeRange === 'week') {
      // Weekly data - map by day of week
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const data = days.map(day => ({ label: day, leads: 0, leadsPrev: 0 }));
      
      // Get current day of week (0 = Sunday, 1 = Monday, etc.)
      const today = new Date().getDay();

      if (stats.weekDaily) {
        stats.weekDaily.forEach(d => {
          const dow = parseInt(d.dow);
          if (data[dow]) {
            data[dow].leads = parseInt(d.leads) || 0;
          }
        });
      }

      if (stats.lastWeekDaily) {
        stats.lastWeekDaily.forEach(d => {
          const dow = parseInt(d.dow);
          if (data[dow]) {
            data[dow].leadsPrev = parseInt(d.leads) || 0;
          }
        });
      }

      // Reorder to start from Monday
      const reordered = [...data.slice(1), data[0]];
      
      // Set future days of current week to null (so they don't show in chart)
      // Monday = index 0, Tuesday = 1, ..., Sunday = 6
      const todayMondayBased = today === 0 ? 6 : today - 1; // Convert Sunday=0 to Monday-based
      for (let i = todayMondayBased + 1; i < 7; i++) {
        reordered[i].leads = 0;
      }
      
      return reordered;

    } else if (chartTimeRange === 'month') {
      // Monthly data
      const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
      const data = Array.from({ length: daysInMonth }, (_, i) => ({
        label: (i + 1).toString(),
        leads: 0,
        leadsPrev: 0
      }));

      if (stats.monthDaily) {
        stats.monthDaily.forEach(d => {
          const dayIndex = parseInt(d.day) - 1;
          if (data[dayIndex]) {
            data[dayIndex].leads = parseInt(d.leads) || 0;
          }
        });
      }

      if (stats.lastMonthDaily) {
        stats.lastMonthDaily.forEach(d => {
          const dayIndex = parseInt(d.day) - 1;
          if (data[dayIndex]) {
            data[dayIndex].leadsPrev = parseInt(d.leads) || 0;
          }
        });
      }

      return data;
    }

    return [];
  };

  const getChartComparison = () => {
    const today = new Date();
    const currentMonthName = today.toLocaleDateString('en-US', { month: 'long' });
    const prevMonth = new Date(today);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const prevMonthName = prevMonth.toLocaleDateString('en-US', { month: 'long' });

    switch (chartTimeRange) {
      case 'today': return { currentLabel: 'Today', previousLabel: 'Yesterday' };
      case 'lastWeekday': return { currentLabel: `This ${todayWeekday}`, previousLabel: `Last ${todayWeekday}` };
      case 'week': return { currentLabel: 'This Week', previousLabel: 'Last Week' };
      case 'month': return { currentLabel: currentMonthName, previousLabel: prevMonthName };
      default: return { currentLabel: 'Current', previousLabel: 'Previous' };
    }
  };

  const chartData = prepareChartData();
  const chartComparison = getChartComparison();

  const chartTotals = {
    current: chartData.reduce((sum, d) => sum + (d.leads || 0), 0),
    previous: chartData.reduce((sum, d) => sum + (d.leadsPrev || 0), 0)
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const StatCard = ({ title, value, icon: Icon, color, comparison, subtitle }) => (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography color="text.secondary" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{title}</Typography>
            <Typography variant="h4" fontWeight={700} color={color} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>{value}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{subtitle}</Typography>}
            {comparison !== undefined && (
              <Box display="flex" alignItems="center" mt={0.5}>
                {comparison >= 0 ? <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} /> : <TrendingDown sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />}
                <Typography variant="body2" color={comparison >= 0 ? 'success.main' : 'error.main'} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {comparison >= 0 ? '+' : ''}{comparison}% vs yesterday
                </Typography>
              </Box>
            )}
          </Box>
          {!isMobile && (
            <Box sx={{ bgcolor: `${color}15`, borderRadius: 2, p: 1 }}>
              <Icon sx={{ color: color, fontSize: { xs: 24, sm: 28 } }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const PeriodButton = ({ value, label }) => (
    <Box
      onClick={() => handlePeriodChange(value)}
      sx={{
        px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 }, borderRadius: 1, cursor: 'pointer',
        bgcolor: period === value ? 'primary.main' : 'grey.100',
        color: period === value ? 'white' : 'text.primary',
        fontWeight: period === value ? 600 : 400,
        fontSize: { xs: '0.7rem', sm: '0.875rem' },
        '&:hover': { bgcolor: period === value ? 'primary.dark' : 'grey.200' }
      }}
    >
      {label}
    </Box>
  );

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  }

  const todayLeads = parseInt(stats?.today?.total_leads || 0);
  const yesterdayLeads = parseInt(stats?.yesterday?.total_leads || 0);
  const comparison = yesterdayLeads > 0 ? Math.round(((todayLeads - yesterdayLeads) / yesterdayLeads) * 100) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Facebook Lead Ads Dashboard
      </Typography>

      {/* Period Filters */}
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={3}>
        <PeriodButton value="today" label="Today" />
        <PeriodButton value="yesterday" label="Yesterday" />
        <PeriodButton value="week" label="7 Days" />
        <PeriodButton value="month" label="30 Days" />
      </Box>

      {/* KPI Cards */}
      <Box display="grid" gridTemplateColumns={{ xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }} gap={{ xs: 1.5, sm: 2 }} mb={3}>
        <StatCard title="Total Leads" value={stats?.total?.total_leads || 0} icon={People} color="#3B82F6" comparison={period === 'today' ? comparison : undefined} />
        <StatCard title="Positive" value={stats?.total?.positive_leads || 0} icon={CheckCircle} color="#10B981" subtitle={`${stats?.total?.total_leads > 0 ? Math.round((stats?.total?.positive_leads / stats?.total?.total_leads) * 100) : 0}%`} />
        <StatCard title="Pending" value={stats?.total?.pending_leads || 0} icon={HourglassEmpty} color="#F59E0B" />
        <StatCard title="Trash" value={stats?.total?.trash_leads || 0} icon={Delete} color="#EF4444" />
        <StatCard title="Quality Score" value={Math.round(stats?.total?.avg_quality_score || 0)} icon={Star} color="#8B5CF6" subtitle="out of 100" />
      </Box>

      {/* Charts */}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '2fr 1fr' }} gap={{ xs: 2, sm: 3 }}>
        {/* Trend Chart */}
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Lead Trend</Typography>
              <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 220 } }}>
                <Select value={chartTimeRange} onChange={(e) => setChartTimeRange(e.target.value)} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  <MenuItem value="today">Today vs Yesterday</MenuItem>
                  <MenuItem value="lastWeekday">Today vs Last {todayWeekday}</MenuItem>
                  <MenuItem value="week">This Week vs Last</MenuItem>
                  <MenuItem value="month">This Month vs Last</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorLeadsCurrent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorLeadsPrev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#9ca3af" style={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }} tickLine={false} interval={chartTimeRange === 'month' ? (isMobile ? 4 : 2) : (chartTimeRange === 'today' || chartTimeRange === 'lastWeekday' ? (isMobile ? 4 : 2) : 0)} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} hide={isMobile} />
                <Tooltip formatter={(value, name) => {
                  const labels = { leads: chartComparison.currentLabel, leadsPrev: chartComparison.previousLabel };
                  return [Math.round(value), labels[name] || name];
                }} />
                <Area type="monotone" dataKey="leadsPrev" stroke="#93c5fd" strokeWidth={2} fill="url(#colorLeadsPrev)" strokeDasharray="5 5" name={chartComparison.previousLabel} />
                <Area type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} fill="url(#colorLeadsCurrent)" name={chartComparison.currentLabel} />
              </AreaChart>
            </ResponsiveContainer>
            <Box display="flex" justifyContent="center" gap={{ xs: 1, sm: 3 }} mt={1} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {chartComparison.currentLabel}: <strong>{chartTotals.current}</strong> vs {chartComparison.previousLabel}: <strong>{chartTotals.previous}</strong>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Top Campaigns */}
        {!isTablet && (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Top Campaigns</Typography>
              {stats?.top_campaigns?.length > 0 ? (
                stats.top_campaigns.map((campaign, index) => (
                  <Box key={campaign.id} sx={{ mb: 2, pb: 2, borderBottom: index < stats.top_campaigns.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box flex={1} mr={2}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem' }}>{campaign.campaign_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{campaign.advertiser_name}</Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="body2" fontWeight={700} color="primary">{campaign.total_leads} leads</Typography>
                        <Chip
                          size="small"
                          label={`${campaign.positive_leads} pos`}
                          sx={{
                            fontSize: '0.65rem',
                            height: 18,
                            bgcolor: 'success.main',
                            color: 'white',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>No active campaigns</Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}

export default FbDashboard;

